import { useId, type CSSProperties } from 'react';
import { cn } from '@/components/ui/cn';
import { useObserver, type ObserverState } from './observerContext';

/*
  Внешность «Наблюдателя» (docs/observer-design.md).

  Слои снаружи внутрь, у каждого своя работа — иначе анимации дерутся за transform:
    root   — позиция и размер (пишет движок),
    tilt   — деформация в движении,
    breath — дыхание в покое,
    skin   — цвет и знак состояния.

  Два принципа внешности:
  1. В режиме выделения он НЕ закрашивает элемент: заливка полупрозрачная, границу держат
     рамка и полоса у левого края, поэтому текст пункта читается сквозь него.
  2. Состояния показываются радиально: пульсирующее ядро, расходящиеся или сходящиеся кольца,
     вращающийся сектор. Никаких полосок и линейных индикаторов.
*/

const STATE_COLOR: Record<ObserverState, string> = {
  idle: 'var(--brand)',
  thinking: 'var(--ai)',
  writing: 'var(--info)',
  download: 'var(--info)',
  upload: 'var(--brand)',
  update: 'var(--ai-strong)',
  asking: 'var(--brand)',
  success: 'var(--success)',
  error: 'var(--danger)',
};

function Ripple({ direction, delay }: { direction: 'out' | 'in'; delay: string }) {
  return (
    <span
      aria-hidden
      style={{ animationDelay: delay } as CSSProperties}
      className={cn(
        'pointer-events-none absolute inset-0 observer-ripple motion-reduce:hidden',
        direction === 'out' ? 'animate-observer-ripple-out' : 'animate-observer-ripple-in',
      )}
    />
  );
}

/** Знак состояния: ядро, кольца и сектор. Всё радиальное. */
function StateMark({ state }: { state: ObserverState }) {
  // Вихрь закручивается на всём, что работает прямо сейчас: мысль, обновление, передача данных.
  const spinning = state === 'thinking' || state === 'update' || state === 'download' || state === 'upload';
  const fastCore = state === 'error' || state === 'writing';

  return (
    <>
      {spinning && (
        <span
          aria-hidden
          className="absolute inset-0 animate-observer-spin observer-conic motion-reduce:animate-none"
        />
      )}
      {state === 'download' && (
        <>
          <Ripple direction="in" delay="0ms" />
          <Ripple direction="in" delay="600ms" />
        </>
      )}
      {(state === 'upload' || state === 'update' || state === 'success') && (
        <>
          <Ripple direction="out" delay="0ms" />
          <Ripple direction="out" delay="600ms" />
        </>
      )}
      {state === 'error' && <Ripple direction="out" delay="0ms" />}
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 rounded-full observer-core',
          fastCore ? 'animate-observer-core-fast' : 'animate-observer-core',
          'motion-reduce:animate-none',
        )}
      />
    </>
  );
}

/**
 * Шар для длительной операции: сфера со светом, закрученной атмосферой и кольцом-орбитой.
 * Красится цветом текущего состояния, поэтому долгая операция и её исход читаются одинаково.
 * Обороты заметные: медленное вращение на семь секунд выглядело неподвижным.
 */
function Planet() {
  const id = useId();
  return (
    <svg viewBox="0 0 48 48" className="size-full overflow-visible" aria-hidden>
      <defs>
        <clipPath id={`${id}-c`}>
          <circle cx="24" cy="24" r="17" />
        </clipPath>
        <radialGradient id={`${id}-body`} cx="34%" cy="26%" r="80%">
          <stop offset="0%" stopColor="var(--fg-inverse)" stopOpacity="0.55" />
          <stop offset="28%" stopColor="var(--body, var(--brand))" />
          <stop offset="100%" stopColor="var(--brand-deep)" />
        </radialGradient>
        <radialGradient id={`${id}-glow`} cx="50%" cy="50%" r="50%">
          <stop offset="58%" stopColor="var(--body, var(--brand))" stopOpacity="0" />
          <stop offset="100%" stopColor="var(--body, var(--brand))" stopOpacity="0.45" />
        </radialGradient>
      </defs>

      {/* Свечение дышит вместе с корпусом */}
      <circle
        cx="24"
        cy="24"
        r="23"
        fill={`url(#${id}-glow)`}
        className="origin-center animate-observer-core motion-reduce:animate-none"
      />
      <circle cx="24" cy="24" r="17" fill={`url(#${id}-body)`} />

      {/* Атмосфера: широкие полосы облаков, заметно проплывающие по диску */}
      <g clipPath={`url(#${id}-c)`}>
        <g className="origin-center animate-observer-swirl motion-reduce:animate-none">
          <ellipse cx="24" cy="16" rx="21" ry="5" fill="var(--fg-inverse)" opacity="0.3" />
          <ellipse cx="24" cy="29" rx="19" ry="4" fill="var(--fg-inverse)" opacity="0.2" />
        </g>
        <g className="origin-center animate-observer-swirl-slow motion-reduce:animate-none">
          <ellipse cx="21" cy="24" rx="17" ry="7" fill="var(--brand-deep)" opacity="0.35" />
          <ellipse cx="27" cy="34" rx="14" ry="4" fill="var(--fg-inverse)" opacity="0.16" />
        </g>
        {/* Блик остаётся на месте: он от источника света, а не от вращения */}
        <circle cx="17" cy="16" r="6" fill="var(--fg-inverse)" opacity="0.28" />
      </g>

      {/* Кольцо-орбита */}
      <ellipse
        cx="24"
        cy="24"
        rx="22"
        ry="6"
        fill="none"
        stroke="var(--body, var(--brand))"
        strokeWidth="1.5"
        opacity="0.7"
        className="origin-center animate-observer-swirl-slow motion-reduce:animate-none"
      />
    </svg>
  );
}

export function Observer() {
  const { settings, state, shape, placement, onActivate, rootRef, tiltRef, skinRef } = useObserver();
  if (!settings.enabled) return null;

  const interactive = settings.interactive && !!onActivate;
  const covering = placement === 'cover';
  const breathing = settings.presence === 'companion' && !settings.calm && !covering;
  // Пока идёт работа, корпус держит вытянутую позу: это видно даже боковым зрением.
  const busy =
    !settings.calm &&
    (state === 'download' || state === 'upload' || state === 'update' || state === 'thinking');

  return (
    <div
      ref={rootRef}
      aria-hidden={!interactive}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? 'Спросить помощника про этот экран' : undefined}
      onClick={interactive ? () => onActivate?.() : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onActivate?.();
              }
            }
          : undefined
      }
      data-slot="observer"
      className={cn(
        'fixed top-0 left-0 z-fab transition-observer motion-reduce:transition-none',
        interactive
          ? 'pointer-events-auto cursor-pointer focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
          : 'pointer-events-none',
      )}
    >
      <div ref={tiltRef} data-phase="rest" className="size-full observer-tilt">
        <div className={cn('size-full', breathing && 'animate-observer-breathe motion-reduce:animate-none')}>
          {/* Поза: пока идёт работа, корпус вытянут вверх и мягко колеблется, как упругий мяч */}
          <div
            className={cn(
              'size-full',
              busy && !covering && 'animate-observer-busy motion-reduce:animate-none',
            )}
          >
            <div
              ref={skinRef}
              data-state={state}
              data-shape={shape}
              data-placement={placement}
              data-presence={settings.presence}
              style={{ '--body': STATE_COLOR[state] } as CSSProperties}
              className={cn(
                'relative size-full',
                covering ? 'rounded-md observer-cover' : 'rounded-full',
                !covering && shape !== 'planet' && 'observer-skin shadow-brand',
              )}
            >
              {shape === 'planet' && !covering ? (
                <Planet />
              ) : covering ? (
                // В режиме выделения знак состояния живёт маленькой точкой у правого края,
                // чтобы не мешать тексту пункта.
                <span
                  aria-hidden
                  className="absolute top-1/2 right-2 size-2 -translate-y-1/2 animate-observer-core rounded-full observer-skin motion-reduce:animate-none"
                />
              ) : (
                <>
                  <StateMark state={state} />
                  {/* Отражение-серп: обращено к курсору, поэтому читается как взгляд */}
                  <span
                    className="pointer-events-none absolute inset-0 rounded-full observer-glare"
                    aria-hidden
                  />
                  {/* Вспышка при смене состояния: переключение видно сразу */}
                  <span
                    key={state}
                    aria-hidden
                    className="pointer-events-none absolute inset-0 animate-observer-burst observer-ripple motion-reduce:hidden"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
